<template>
  <div class="form-group">
    <label :for="name">{{
      props.label ? t(`labels.${props.label}`) : t(`labels.${props.name}`)
    }}</label>

    <Textarea
      :id="name"
      v-model="displayedValue"
      class="w-full my-2"
      :placeholder="
        props.placeholder
          ? translatePrefixedOrValue(i18n, 'placeholder', props.placeholder)
          : props.placeholder
      "
      :rows="props.compact ? 1 : 4"
      :disabled="disabled"
      :class="{
        'dirty-state': (isFieldDirty || differsFromGlobal) && !disabled,
        'error-state': !!errorMessage && !disabled,
      }"
      @blur="() => emit('blur')"
    />

    <!-- Show selectable flags (only in edit mode) -->
    <div v-if="!disabled && isMultilang" class="mt-0 flex gap-2 flex-wrap">
      <Button
        v-for="locale in missingLocales"
        :key="locale"
        v-tooltip="{
          value: t('titletags.addLang', { locale: t(`enums.${locale.toUpperCase()}`) }),
          showDelay: 300,
        }"
        variant="text"
        type="button"
        class="px-2 py-1 rounded border text-m"
        @click="addLocaleFlag(locale)"
      >
        {{ localeToFlagMap[locale] }}
      </Button>
    </div>

    <FormError v-if="!disabled" :message="errorMessage" />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import Textarea from 'primevue/textarea';
import { useField } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import { useMultilingualText } from '@/composables/useMultilingualText';
import { translatePrefixedOrValue } from '@/utils/i18n';
import { localeToFlagMap } from '@/utils/multilingualCore';

import FormError from './errors/FormError.vue';

const props = defineProps<{
  name: string;
  modelValue: string | undefined;
  initialValue?: string | undefined;
  globalDefault?: string;
  placeholder?: string;
  isDirty?: (dirty: boolean) => void;
  label?: string;
  disabled?: boolean;
  multiLanguage?: boolean;
  compact?: boolean;
}>();

const i18n = useI18n();
const { t } = i18n;
const { errorMessage } = useField<string>(props.name);
const internalValue = ref(props.modelValue);
const initialValue = computed(() => props.initialValue ?? '');
const isFieldDirty = ref(false);

const differsFromGlobal = computed(() => {
  return props.globalDefault !== undefined && props.globalDefault !== null
    ? internalValue.value !== props.globalDefault
    : false;
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'blur'): void;
}>();
const emitIsDirty = props.isDirty ?? (() => {});

watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue;
  },
);

watch(internalValue, (newValue) => {
  emit('update:modelValue', newValue ?? '');
});

watch([internalValue, initialValue], () => {
  const isDirty = internalValue.value !== initialValue.value;
  isFieldDirty.value = isDirty;
  emitIsDirty(isDirty);
});

// Multilingual logic
const { getTextForLocale, getAvailableLocales } = useMultilingualText();
const isMultilang = computed(() => props.multiLanguage);
const localizedText = computed(() => getTextForLocale(internalValue.value));
const availableLocales = computed(() => getAvailableLocales(internalValue.value));
const allLocales = Object.keys(localeToFlagMap).filter((l) => l !== 'de');
const missingLocales = computed(() =>
  allLocales.filter((locale) => !availableLocales.value.includes(locale)),
);

function addLocaleFlag(locale: string) {
  const flag = localeToFlagMap[locale];
  if (flag && !internalValue.value?.includes(flag)) {
    internalValue.value = (internalValue.value?.trim() ?? '') + ' ' + flag + ' ';
  }
}

const displayedValue = computed({
  get() {
    return props.disabled && isMultilang.value ? localizedText.value : internalValue.value;
  },
  set(value: string) {
    internalValue.value = value;
  },
});
</script>
